'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import classNames from 'classnames';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { USE_CLASSES as BUTTON_USE_CLASSES } from '../button/ButtonConstants';
import { Consumer as FieldsetContextConsumer } from '../context/FieldsetContext';
import SyntheticEvent from '../core/SyntheticEvent';
import Controllable from '../decorators/Controllable';
import UIAbstractDropdown from '../dropdown/abstract/UIAbstractDropdown';
import { PLACEMENTS } from '../tooltip/PlacementConstants';
import UIGroupedTypeahead from '../typeahead/UIGroupedTypeahead';
import UITypeahead from '../typeahead/UITypeahead';
import lazyEval from '../utils/lazyEval';
import createLazyPropType from '../utils/propTypes/createLazyPropType';
import refObject from '../utils/propTypes/refObject';
import { INPUT_SIZE_OPTIONS } from './InputConstants';
import { getButtonProps } from './utils/SelectOptionUtils';
import Deprecated from '../decorators/Deprecated';
import ShareInput from '../decorators/ShareInput';

class UISearchableSelectInput extends Component {
  constructor(...args) {
    super(...args);

    this.focus = () => {
      this.setIsOpen(true);
    };

    this.handleFocusLeave = () => {// Temporarily disabled. This causes close-and-reopen when clicking the anchor.
      // this.setIsOpen(false);
    };

    this.handleSelect = evt => {
      const {
        onChange,
        value
      } = this.props;

      if (evt.target.value !== value) {
        onChange(evt);
      }

      this.setIsOpen(false);
    };

    this.setIsOpen = nextOpen => {
      const {
        onOpenChange
      } = this.props;
      onOpenChange(SyntheticEvent(nextOpen));
    };
  }

  render() {
    const _this$props = this.props,
          {
      autoFocus,
      children,
      className,
      dropdownClassName,
      error,
      inputRef,
      itemComponent,
      layout,
      minimumSearchCount,
      onBlur,
      onSearchChange,
      options,
      placeholder,
      typeaheadPlaceholder,
      value
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["autoFocus", "children", "className", "dropdownClassName", "error", "inputRef", "itemComponent", "layout", "minimumSearchCount", "onBlur", "onSearchChange", "options", "placeholder", "typeaheadPlaceholder", "value"]);

    this.hasBeenClosed = this.hasBeenClosed || this.props.open === false;
    const Typeahead = layout === 'grouped' ? UIGroupedTypeahead : UITypeahead;
    const computedValue = value == null ? '' : value;
    const {
      buttonText,
      valueIsValid
    } = getButtonProps({
      options,
      placeholder,
      resetValue: '',
      value: computedValue
    });
    const computedDropdownClassName = classNames('private-searchable-select-input__dropdown', dropdownClassName);
    return /*#__PURE__*/_jsx(FieldsetContextConsumer, {
      children: ({
        size: fieldSize
      }) => /*#__PURE__*/_jsxs(UIAbstractDropdown, Object.assign({}, rest, {
        "aria-invalid": !valueIsValid,
        autoFocus: autoFocus,
        buttonText: buttonText,
        className: classNames(className, INPUT_SIZE_OPTIONS[fieldSize], !valueIsValid && "invalid", error && 'private-form__control--error', computedValue === '' && "noValue"),
        dropdownClassName: computedDropdownClassName,
        onFocusLeave: this.handleFocusLeave,
        pinToConstraint: ['top', 'bottom'],
        children: [children, /*#__PURE__*/_jsx(Typeahead, {
          autoFocus: autoFocus || this.hasBeenClosed,
          inputRef: inputRef,
          itemComponent: itemComponent,
          minimumSearchCount: minimumSearchCount,
          onBlur: onBlur,
          onChange: this.handleSelect,
          onSearchChange: onSearchChange,
          options: options,
          placeholder: lazyEval(typeaheadPlaceholder),
          value: value
        })]
      }))
    });
  }

}

UISearchableSelectInput.displayName = 'UISearchableSelectInput';
UISearchableSelectInput.propTypes = {
  autoFocus: PropTypes.bool,
  buttonUse: PropTypes.oneOf(Object.keys(BUTTON_USE_CLASSES)).isRequired,
  children: PropTypes.node,
  disabled: UIAbstractDropdown.propTypes.disabled,
  dropdownClassName: PropTypes.string,
  error: PropTypes.bool,
  inputRef: refObject.isRequired,
  itemComponent: PropTypes.elementType,
  layout: PropTypes.oneOf(['list', 'grouped']),
  menuWidth: UIAbstractDropdown.propTypes.menuWidth,
  minimumSearchCount: PropTypes.number,
  onChange: PropTypes.func,
  onOpenChange: PropTypes.func,
  onOpenComplete: PropTypes.func,
  onSearchChange: PropTypes.func,
  open: UIAbstractDropdown.propTypes.open,
  options: UITypeahead.propTypes.options,
  placeholder: PropTypes.node,
  placement: PropTypes.oneOf(PLACEMENTS),
  popoverRef: refObject,
  readOnly: PropTypes.bool,
  typeaheadPlaceholder: createLazyPropType(PropTypes.string).isRequired,
  value: UITypeahead.propTypes.value
};
UISearchableSelectInput.defaultProps = {
  buttonUse: 'form',
  itemComponent: 'li',
  layout: 'list',
  minimumSearchCount: 8,
  open: false,
  placement: 'bottom',
  typeaheadPlaceholder: () => I18n.text('salesUI.UISearchableSelectInput.placeholder'),
  value: ''
};
export default Deprecated('Use `UISelect` instead.')(ShareInput(Controllable(UISearchableSelectInput, ['open', 'value'])));